import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { formatStandardTimestamp } from "@/lib/dateFormat";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { QuoteComparison } from "@/components/QuoteComparison";
import { getQuoteBlockers } from "@/data/unifiedBlockers";
import { getQuoteNextSteps } from "@/data/nextStepEngine";
import { DISPOSITION_REASONS, getDefaultFollowUpDate } from "@/data/opportunityTransitions";
import { RecordHealthPanel } from "@/components/RecordHealthPanel";
import { CustomerQuoteView } from "@/components/CustomerQuoteView";
import { ContractApprovalDialog, ContractCancelDialog } from "@/components/ContractApprovalDialog";
import { ContractDocumentView } from "@/components/ContractDocumentView";
import { VersionTimeline } from "@/components/VersionTimeline";
import { buildLineageFromRecords } from "@/data/versioningSystem";
import { CustomerPropertyVerification } from "@/components/CustomerPropertyVerification";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { useCatalog } from "@/data/CatalogContext";
import { usePricing } from "@/data/PricingContext";
import type { QuoteStatus, PricingGuardrails, DepositInfo, ContractDocument, CustomerVerification, CancelReason } from "@/data/quoteTypes";
import { computePricingStatus, PRICING_STATUS_STYLES, EMPTY_VERIFICATION, isVerificationComplete, getDepositDefaults, isDepositValid, PAYMENT_METHODS, CANCEL_REASON_LABELS, isWonStatus, isClosedStatus, isActiveStatus } from "@/data/quoteTypes";
import type { ProjectConfiguration } from "@/data/types";
import type { MaterialsStatus } from "@/data/types";
import { calculatePrice, type ComplexityFactors, type PricingOverrides } from "@/data/pricingEngine";
import { deriveJobFields } from "@/data/buildConfigMapping";
 import { generateMaterialListAudit, toMaterialLineItems, computeMaterialsReadiness } from "@/data/skuMapping";
 import { getCanonicalSpecForArchetype, bindSpec } from "@/data/jobSpec";
 import { useMaterialDeltas } from "@/hooks/useMaterialDeltas";
 import type { JobSpec } from "@/data/jobSpec";
import type { SpecArchetype } from "@/data/contractorPayProfile";
import { generateDraftPO, createStagingRecord, createLoadOutChecklist } from "@/data/warehouseExecution";
import { calculateRescissionEnd } from "@/data/rescission";
import { ProjectConfigurator } from "@/components/ProjectConfigurator";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { isUuidLike } from "@/lib/profileDisplayName";
import { parseAddressPartsFromText } from "@/types/mappers/quote.mapper";
import { PortalInvitationDialog } from "@/components/PortalInvitationDialog";
import { PermissionGate } from "@/components/PermissionGate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, ArrowRight, User, Mail, Phone, MapPin, Clock, ClipboardList,
  CheckCircle, FileText, PenLine, Send, XCircle, Sparkles, DollarSign, Target,
  ShieldCheck, Shield, AlertTriangle, Lock, Presentation, Trophy, Ban, Layers, Eye, CreditCard, Pen,
  Building, Check, Wrench, RefreshCw, CalendarClock, ChevronRight,
  Camera, ImagePlus, X, Plus, Ruler, Download, ZoomIn,
} from "lucide-react";
import { toast } from "sonner";
import { LifecycleTracker } from "@/components/LifecycleTracker";
import { StorageThumbnailImage } from "@/components/media/StorageThumbnailImage";
import { ActionRequiredPanel, type ActionRequiredItem } from "@/components/ActionRequiredPanel";
// OpportunityDetailFrame import removed (Phase 1 Opportunity Decomposition).
// Pre-sale renders QuoteDetail directly via QuoteDetailRoute; closed states
// render OpportunityReadOnlySummary. Frame remains in tree but unused.
import { cn } from "@/lib/utils";
import { JobService } from "@/services/job.service";
import { LeadService } from "@/services/lead.service";
import { CustomerService } from "@/services/customer.service";
import { replaceJobMaterialsSnapshot } from "@/services/inventory.service";
import { uploadPrivateAttachment, deletePrivateAttachment, createSignedAttachmentUrl, getAttachments } from "@/services/attachment.service";
import { supabase } from "@/lib/supabase";
import type { Quote } from "@/data/quoteTypes";
import type { Opportunity, OpportunityStage, StageChange } from "@/data/opportunityTypes";

/* ── Wiring props ── */
interface QuoteDetailProps {
  /** When provided, bypasses useAppData() lookup and uses this as source of truth */
  initialQuote?: Quote | Opportunity;
  /** When provided, persists updates to Supabase instead of setQuotes() */
  onPersistUpdate?: (id: string, updates: Partial<Quote>) => Promise<void>;
}

/* ── Constants ── */
const STATUS_OPTIONS: QuoteStatus[] = ["Appointment Completed", "Follow-Up Needed", "Closed Won", "Closed Lost"];
const STATUS_COLORS: Record<QuoteStatus, string> = {
  "Lead": "bg-info/10 text-info",
  "Quote Created": "bg-cyan-100 text-cyan-800",
  "Appointment Scheduled": "bg-warning/10 text-warning",
  "Appointment Completed": "bg-primary/10 text-primary",
  "Follow-Up Needed": "bg-warning/10 text-warning",
  "Closed Won": "bg-success/10 text-success",
  "Closed Lost": "bg-destructive/10 text-destructive",
};
const STATUS_ICONS: Record<QuoteStatus, React.ElementType> = {
  "Lead": Target,
  "Quote Created": FileText,
  "Appointment Scheduled": Clock,
  "Appointment Completed": Clock,
  "Follow-Up Needed": AlertTriangle,
  "Closed Won": CheckCircle,
  "Closed Lost": XCircle,
};

const DEFAULT_CONFIG: ProjectConfiguration = {
  layoutType: "", wallCount: 3, hasWindow: false, obstructions: [],
  wallMeasurements: { wallA: { width: "", height: "" }, wallB: { width: "", height: "" }, wallC: { width: "", height: "" }, additional: [] },
  structuralChanges: { removingWalls: false, addingWalls: false, movingDrain: false, movingPlumbing: false, subfloorMod: false, windowMod: "N/A" },
  autoProjectType: "", enclosureType: "",
  selectedVerticals: [],
  bathroomScopes: [], bathroomPhase2Addons: [], bathroomMaterials: [],
  bathroomSystemType: "", bathroomBaseSize: "", bathroomCustomSize: "",
  baseWidth: "30", baseLength: "60", baseType: "", baseStyle: "",
  laborComplexity: "standard", plumbingRelocation: false, customModifications: false,
  manualAdjustment: "", manualAdjustmentNotes: "",
  selectedWallSystem: "", selectedBase: "", selectedGrabBars: [],
  selectedGlass: "", selectedDoor: "", selectedFixture: "",
  selectedAddOns: [], customQuoteItems: [],
  projectCategory: "", workDescription: "", areas: [],
  totalSqFt: "", roomCount: "", measurementNotes: "",
  primaryMaterial: "", materialGrade: "", colorSelection: "", wallColor: "", baseColor: "", drainLocation: "", additionalOptions: [],
  basePrice: "", discount: "", discountType: "percent",
  hardwareFinish: "", hardwareOverrides: {}, discounts: { military: false, senior: false, frontliner: false },
  customAddOns: [], noAddOnsSelected: false, consultationCategory: "",
  fullBathroomSelections: { flooring: "", toilet: "", vanity: "" },
  fullBathroomAddOns: [], fullBathroomCustomAddOns: [],
  windowCount: 0, windowTypes: [], doorEntryType: "", doorSize: "",
  additionalCharges: "", additionalChargesNote: "",
  specialConditions: "", customerRequests: "", internalNotes: "",
  status: "draft", lastUpdated: new Date().toISOString(),
};

/* ── Site Documentation ── */
const SITE_PHOTOS_BUCKET = "job-documents"; // private bucket — access via signed URLs only

interface SitePhoto {
  id: string;
  category: "project_area" | "measurement" | "obstruction";
  preview: string;
  name: string;
  capturedAt: string;
}

const PHOTO_CATEGORIES: { id: SitePhoto["category"]; label: string; icon: React.ElementType; emptyLabel: string }[] = [
  { id: "project_area",  label: "Full Project Area",          icon: Camera,    emptyLabel: "Capture full project area" },
  { id: "measurement",   label: "Measurement Reference",      icon: Ruler,     emptyLabel: "Add measurement reference photos" },
  { id: "obstruction",   label: "Obstructions / Special Conditions", icon: AlertTriangle, emptyLabel: "Document obstructions or special conditions" },
];

/* ── Wizard Steps ── */
interface WizardStep {
  id: string;
  label: string;
  icon: React.ElementType;
  description: string;
}

const WIZARD_STEPS: WizardStep[] = [
  { id: "customer", label: "Customer Info", icon: User, description: "Verify customer identity, address, and property details" },
  { id: "scope", label: "Project Scope", icon: Layers, description: "Select project type and configure the build" },
  { id: "pricing", label: "Pricing", icon: DollarSign, description: "Review pricing breakdown and adjustments" },
  { id: "review", label: "Review & Present", icon: Sparkles, description: "Final review and presentation to homeowner" },
];

function mergeQuoteUpdates(
  current: Quote,
  updates: Partial<Quote>,
  actor: string,
  changedAtIso: string,
): Quote {
  const mergedProjectConfig = updates.projectConfig !== undefined
    ? { ...(current.projectConfig || DEFAULT_CONFIG), ...updates.projectConfig }
    : current.projectConfig;
  const mergedPricingGuardrails = updates.pricingGuardrails !== undefined
    ? { ...(current.pricingGuardrails || {}), ...updates.pricingGuardrails }
    : current.pricingGuardrails;
  const mergedPricingOverride = updates.pricingOverride !== undefined
    ? { ...(current.pricingOverride || {}), ...updates.pricingOverride }
    : current.pricingOverride;
  const mergedVerification = updates.verification !== undefined
    ? { ...(current.verification || {}), ...updates.verification }
    : current.verification;
  const mergedDeposit = updates.deposit !== undefined
    ? { ...(current.deposit || {}), ...updates.deposit }
    : current.deposit;
  const mergedContract = updates.contract !== undefined
    ? { ...(current.contract || {}), ...updates.contract }
    : current.contract;

  const nextStage = (updates.stage ?? updates.status ?? current.stage) as OpportunityStage;
  const currentStage = current.stage as OpportunityStage;
  const nextStatus = (updates.status ?? updates.stage ?? current.status) as QuoteStatus;

  let stageHistory = updates.stageHistory ?? current.stageHistory ?? [];
  if (nextStage && nextStage !== currentStage && updates.stageHistory === undefined) {
    const appended: StageChange = {
      id: crypto.randomUUID(),
      fromStage: currentStage || null,
      toStage: nextStage,
      changedAt: changedAtIso,
      changedBy: actor || current.assignedRep || current.createdBy || "Unknown",
    };
    stageHistory = [...stageHistory, appended];
  }

  return {
    ...current,
    ...updates,
    projectConfig: mergedProjectConfig,
    pricingGuardrails: mergedPricingGuardrails,
    pricingOverride: mergedPricingOverride,
    verification: mergedVerification,
    deposit: mergedDeposit,
    contract: mergedContract,
    stage: nextStage,
    status: nextStatus,
    stageHistory,
    updatedAt: changedAtIso.slice(0, 10),
  };
}


export default function QuoteDetail({ initialQuote, onPersistUpdate }: QuoteDetailProps = {}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    quotes, setQuotes, jobs, setJobs,
    purchaseOrders, setPurchaseOrders, setStagingRecords, setLoadOutChecklists,
    conversations, messages, followUpTasks,
  } = useAppData();

  // DB-wired mode: local optimistic state seeded from initialQuote
  const [localQuote, setLocalQuote] = useState<Quote | null>(
    initialQuote ? (initialQuote as Quote) : null
  );
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { options: allOptions } = useProductOptions();
  const { skuCatalog, mappingRules } = useCatalog();
  const fin$ = useFinancialPermissions();
  const [wizardStep, setWizardStep] = useState(0);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");

  // Follow-up dialog state (stage transition panel)
  const [followUpTransitionOpen, setFollowUpTransitionOpen] = useState(false);
  const [followUpDispositionReason, setFollowUpDispositionReason] = useState("");
  const [followUpType, setFollowUpType] = useState("rep");
  const [followUpDueDate, setFollowUpDueDate] = useState(getDefaultFollowUpDate());
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [comparisonView, setComparisonView] = useState(false);
  const [presentViewOpen, setPresentViewOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [cancelContractOpen, setCancelContractOpen] = useState(false);
  const [portalInviteOpen, setPortalInviteOpen] = useState(false);

  // Deposit state
  const [depositAmount, setDepositAmount] = useState("0");
  const [depositMethod, setDepositMethod] = useState("Financing");
  const [depositDate, setDepositDate] = useState(new Date().toISOString().slice(0, 10));
  const [depositNotes, setDepositNotes] = useState("0 Down Financing");
   const [permitRequiredOverride, setPermitRequiredOverride] = useState(false);

   // Current draft configuration (for real-time delta tracking)
   const [currentConfig, setCurrentConfig] = useState<ProjectConfiguration | null>(null);

   // Site documentation photos — persisted to Supabase Storage (job-documents bucket)
  const [sitePhotos, setSitePhotos] = useState<SitePhoto[]>([]);
  const [lightboxPhoto, setLightboxPhoto] = useState<SitePhoto | null>(null);
  const [uploadingCategory, setUploadingCategory] = useState<SitePhoto["category"] | null>(null);
  const localQuoteRef = useRef(localQuote);
  const quotesRef = useRef(quotes);
  const persistSequenceRef = useRef<Promise<void>>(Promise.resolve());
  const latestOptimisticVersionRef = useRef(0);

  useEffect(() => {
    localQuoteRef.current = localQuote;
  }, [localQuote]);

  useEffect(() => {
    quotesRef.current = quotes;
  }, [quotes]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>, category: SitePhoto["category"]) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !quote?.id || !isUuidLike(quote.id)) return;
    e.target.value = "";

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Not authenticated"); return; }

    setUploadingCategory(category);
    for (const file of files) {
      try {
        const record = await uploadPrivateAttachment(
          file, SITE_PHOTOS_BUCKET, "quote", quote.id, "photo", user.id,
          `site_photo:${category}`,
        );
        const preview = await createSignedAttachmentUrl(record.file_path, SITE_PHOTOS_BUCKET).catch(() => "");
        setSitePhotos(prev => [...prev, {
          id: record.id,
          category,
          preview,
          name: record.file_name,
          capturedAt: record.created_at,
        }]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploadingCategory(null);
  };

  const removeSitePhoto = async (id: string) => {
    setSitePhotos(prev => prev.filter(p => p.id !== id));
    try {
      await deletePrivateAttachment(id, SITE_PHOTOS_BUCKET);
    } catch {
      toast.error("Failed to delete photo — please try again.");
    }
  };

  // DB-wired mode: use localQuote; classic mode: read from AppDataContext
  const rawQuote = initialQuote ? localQuote : quotes.find((q) => q.id === id);
  // Defensive normalization: ensure customerName is derived from primaryName,
  // and projectConfig is merged with safe defaults so downstream consumers
  // (pricingEngine, summaries, wizard, dialogs) never crash on sparse seeds.
  const quote = useMemo(() => {
    if (!rawQuote) return undefined;
    const anyQ = rawQuote as any;
    const customerName = anyQ.customerName || anyQ.primaryName || "";
    const customerPhone = anyQ.customerPhone || anyQ.primaryPhone || "";
    const customerEmail = anyQ.customerEmail || anyQ.primaryEmail || "";
    const customerAddress = anyQ.customerAddress || anyQ.address || "";
    return {
      ...rawQuote,
      // Status is the legacy field name; canonical Opportunity uses `stage`.
      status: (rawQuote as any).status || rawQuote.stage,
      customerName,
      customerPhone,
      customerEmail,
      customerAddress,
      projectConfig: rawQuote.projectConfig
        ? ({ ...DEFAULT_CONFIG, ...rawQuote.projectConfig } as ProjectConfiguration)
        : rawQuote.projectConfig,
    };
  }, [rawQuote]);

  // Load persisted site photos and generate signed URLs for private bucket display
  useEffect(() => {
    if (!quote?.id || !isUuidLike(quote.id)) return;
    getAttachments("quote", quote.id)
      .then(async (records) => {
        const normalizeStoragePath = (rawPath: string, bucket: string): string => {
          if (!rawPath) return "";
          const privateMarker = `/storage/v1/object/sign/${bucket}/`;
          const publicMarker = `/storage/v1/object/public/${bucket}/`;
          if (rawPath.includes(privateMarker)) return rawPath.split(privateMarker)[1]?.split("?")[0] ?? "";
          if (rawPath.includes(publicMarker)) return rawPath.split(publicMarker)[1]?.split("?")[0] ?? "";
          return rawPath;
        };
        const siteRecords = records.filter(
          (r: any) => (r.attachmentType ?? r.attachment_type) === "photo" && (r.notes ?? "").startsWith("site_photo:")
        );
        const photos: SitePhoto[] = await Promise.all(
          siteRecords.map(async (r: any) => {
            const rawPath = String(r.filePath ?? r.file_path ?? "");
            const storagePath = normalizeStoragePath(rawPath, SITE_PHOTOS_BUCKET);
            const preview = await createSignedAttachmentUrl(storagePath, SITE_PHOTOS_BUCKET).catch(() => rawPath);
            return {
              id: r.id,
              category: r.notes.replace("site_photo:", "") as SitePhoto["category"],
              preview,
              name: String(r.fileName ?? r.file_name ?? "site-photo"),
              capturedAt: String(r.createdAt ?? r.created_at ?? new Date().toISOString()),
            };
          })
        );
        setSitePhotos(photos);
      })
      .catch(() => {/* silently ignore — non-critical */});
  }, [quote?.id]);  

  /* ── Pricing ── */
  const { config: pricingConfig } = usePricing();
  const pricingOverrides: PricingOverrides = {
    systemBasePrices: pricingConfig.systemBasePrices,
    dimensionPresets: pricingConfig.dimensionPresets,
    materialMultipliers: pricingConfig.materialMultipliers,
    complexityAdders: pricingConfig.complexityAdders,
  };

  const configSummaryItems = useMemo(() => {
    const cfg = quote?.projectConfig;
    if (!cfg) return [];
    const resolveOptionLabel = (rawValue: string | undefined): string => {
      const value = String(rawValue ?? "").trim();
      if (!value) return "";
      const matched = allOptions.find((opt) => opt.id === value);
      if (matched?.displayName) return matched.displayName;
      if (isUuidLike(value)) return "Unmapped Selection";
      return value;
    };
    const items: { label: string; value: string }[] = [];
    if (cfg.selectedVerticals?.length) items.push({ label: "Verticals", value: cfg.selectedVerticals.map(resolveOptionLabel).join(", ") });
    if (cfg.bathroomScopes?.length) items.push({ label: "Bathroom Scope", value: cfg.bathroomScopes.map(resolveOptionLabel).join(", ") });
    if (cfg.bathroomMaterials?.length) items.push({ label: "Materials", value: cfg.bathroomMaterials.map(resolveOptionLabel).join(", ") });
    if (cfg.bathroomSystemType) items.push({ label: "System Type", value: resolveOptionLabel(cfg.bathroomSystemType) });
    if (cfg.bathroomBaseSize) items.push({ label: "Base Dims", value: `${cfg.baseWidth}" × ${cfg.baseLength}"` });
    if (cfg.baseType) items.push({ label: "Base Type", value: resolveOptionLabel(cfg.baseType) });
    if (cfg.baseStyle) items.push({ label: "Base Style", value: resolveOptionLabel(cfg.baseStyle) });
    if (cfg.selectedWallSystem) items.push({ label: "Wall System", value: resolveOptionLabel(cfg.selectedWallSystem) });
    if (cfg.selectedBase) items.push({ label: "Base Product", value: resolveOptionLabel(cfg.selectedBase) });
    if (cfg.selectedGlass) items.push({ label: "Glass", value: resolveOptionLabel(cfg.selectedGlass) });
    if (cfg.selectedDoor) items.push({ label: "Door", value: resolveOptionLabel(cfg.selectedDoor) });
    if (cfg.selectedFixture) items.push({ label: "Fixture", value: resolveOptionLabel(cfg.selectedFixture) });
    if (cfg.basePrice) items.push({ label: "Total", value: `$${parseFloat(cfg.basePrice).toLocaleString()}` });
    return items.filter((entry) => String(entry.value || "").trim().length > 0);
  }, [quote?.projectConfig, allOptions]);

  const createdByDisplay = useMemo(() => {
    const assigned = String(quote?.assignedRep ?? "").trim();
    if (assigned && !isUuidLike(assigned)) return assigned;
    const created = String(quote?.createdBy ?? "").trim();
    if (created && !isUuidLike(created)) return created;
    return "—";
  }, [quote?.assignedRep, quote?.createdBy]);

  const permitAssessment = useMemo(() => {
    const cfg = quote?.projectConfig;
    if (!cfg) return { required: false, reasons: [] as string[] };

    const reasons: string[] = [];
    const verticals = cfg.selectedVerticals ?? [];
    const scopes = cfg.bathroomScopes ?? [];
    const structural = cfg.structuralChanges;

    if (verticals.includes("Bathroom Project") && scopes.includes("Full Bathroom Remodel")) {
      reasons.push("Full bathroom remodel scope");
    }
    if (structural?.removingWalls) reasons.push("Removing walls");
    if (structural?.addingWalls) reasons.push("Adding walls");
    if (structural?.movingPlumbing) reasons.push("Moving plumbing");
    if (structural?.movingDrain) reasons.push("Moving drain");
    if (structural?.subfloorMod) reasons.push("Subfloor modification");
    if (structural?.windowMod && structural.windowMod !== "N/A") reasons.push("Window modification");
    if (cfg.plumbingRelocation) reasons.push("Plumbing relocation");
    if (verticals.includes("Windows") || verticals.includes("Doors")) reasons.push("Exterior envelope work");

    return { required: reasons.length > 0, reasons };
  }, [quote?.projectConfig]);

  // Sync permit toggle: prefer the persisted quote value; fall back to auto-assessment.
  // Re-runs when the quote loads, the config changes, or the convert dialog opens.
  useEffect(() => {
    const saved = quote?.projectConfig?.permitRequired;
    const value = saved !== undefined ? saved : permitAssessment.required;
    setPermitRequiredOverride(value);
  }, [quote?.projectConfig?.permitRequired, permitAssessment.required]);

   const priceBreakdown = useMemo(() => {
     const cfg = quote?.projectConfig;
     if (!cfg) return null;
     const complexity: ComplexityFactors = {
       laborComplexity: cfg.laborComplexity || "standard",
       plumbingRelocation: cfg.plumbingRelocation || false,
       customModifications: cfg.customModifications || false,
     };
     const manualAdj = parseFloat(cfg.manualAdjustment || "0") || 0;
     return calculatePrice(cfg, allOptions, complexity, manualAdj, cfg.manualAdjustmentNotes || "", pricingOverrides);
   }, [quote?.projectConfig, allOptions, pricingOverrides]);

   // Material cost deltas: compare current draft vs saved configuration
   const materialDeltas = useMaterialDeltas(quote?.projectConfig || null, currentConfig);

   const computedTotal = priceBreakdown?.finalTotal || 0;
  const canManageGuardrails = hasPerm("jobs_edit");

  const pricingStatus = priceBreakdown
    ? computePricingStatus(priceBreakdown.finalTotal, quote?.pricingGuardrails)
    : "Not Set" as const;
  const statusStyle = PRICING_STATUS_STYLES[pricingStatus];

  const prefillData = useMemo(() => {
    if (!quote) return undefined;
    return {
      firstName: (quote.customerName || "").split(" ")[0] || "",
      lastName: (quote.customerName || "").split(" ").slice(1).join(" ") || "",
      phone: quote.customerPhone || "",
      email: quote.customerEmail || "",
      address: quote.customerAddress || "",
    };
  }, [quote?.customerName, quote?.customerPhone, quote?.customerEmail, quote?.customerAddress]);

  const updateQuote = useCallback((updates: Partial<Quote>) => {
    const changedAtIso = new Date().toISOString();
    if (initialQuote && onPersistUpdate) {
      const quoteId = id;
      if (!quoteId) return;

      const previousLocalQuote = localQuoteRef.current;
      const previousContextQuote = quotesRef.current.find((q) => q.id === quoteId);
      const baseQuote = previousLocalQuote ?? previousContextQuote;
      if (!baseQuote) return;

      const mergedQuote = mergeQuoteUpdates(baseQuote, updates, role, changedAtIso);
      const persistUpdates: Partial<Quote> = {
        ...updates,
        stage: mergedQuote.stage,
        status: mergedQuote.status,
        stageHistory: mergedQuote.stageHistory,
      };
      if (updates.projectConfig !== undefined) persistUpdates.projectConfig = mergedQuote.projectConfig;
      if (updates.pricingGuardrails !== undefined) persistUpdates.pricingGuardrails = mergedQuote.pricingGuardrails;
      if (updates.pricingOverride !== undefined) persistUpdates.pricingOverride = mergedQuote.pricingOverride;
      if (updates.verification !== undefined) persistUpdates.verification = mergedQuote.verification;
      if (updates.deposit !== undefined) persistUpdates.deposit = mergedQuote.deposit;
      if (updates.contract !== undefined) persistUpdates.contract = mergedQuote.contract;

      setLocalQuote((prev) => (prev ? mergeQuoteUpdates(prev, updates, role, changedAtIso) : prev));
      setQuotes((prev) => prev.map((q) => (q.id === quoteId ? mergeQuoteUpdates(q as Quote, updates, role, changedAtIso) : q)));

      const saveVersion = ++latestOptimisticVersionRef.current;
      const leadId = typeof mergedQuote.leadId === "string" ? mergedQuote.leadId : undefined;
      const nextStage = persistUpdates.stage ?? persistUpdates.status;

      persistSequenceRef.current = persistSequenceRef.current.catch(() => undefined).then(async () => {
        try {
          await onPersistUpdate(quoteId, persistUpdates);
          if (leadId && typeof nextStage === "string") {
            await LeadService.update(leadId, { stage: nextStage });
          }
        } catch (error) {
          console.warn("[updateQuote] Persist failed; rolling back local state.", error);
          if (latestOptimisticVersionRef.current === saveVersion) {
            setLocalQuote(previousLocalQuote);
            if (previousContextQuote) {
              setQuotes((prev) => prev.map((q) => (q.id === quoteId ? previousContextQuote : q)));
            }
          }
          toast.error("Save failed. Changes were not persisted.");
        }
      });
    } else {
      // Classic: mutate AppDataContext seed data
      setQuotes((prev) =>
        prev.map((q) => q.id === id ? mergeQuoteUpdates(q as Quote, updates, role, changedAtIso) : q)
      );
    }
  }, [id, initialQuote, onPersistUpdate, role, setQuotes]);

  // Stage-aware default tab - computed from quote.stage (handles undefined safely)
  const stageDefaultStep = useMemo(() => {
    if (!quote) return 0;
    switch (quote.stage) {
      case "Lead":
      case "Appointment Scheduled":
        return 0;
      case "Appointment Completed":
        return quote.projectConfig?.selectedVerticals?.length ? 2 : 1;
      case "Follow-Up Needed":
        return 3;
      case "Closed Won":
      case "Closed Lost":
        return 3;
      default:
        return 0;
    }
  }, [quote?.stage, quote?.projectConfig?.selectedVerticals?.length]);

  const didInitTabRef = useRef(false);
  useEffect(() => {
    if (didInitTabRef.current) return;
    if (!quote) return;
    didInitTabRef.current = true;
    setWizardStep(stageDefaultStep);
  }, [quote, stageDefaultStep]);

  if (!quote) return <div className="p-8 text-center text-muted-foreground">Opportunity not found.</div>;

  const isLocked = isClosedStatus(quote.status);
  const isContractSigned = quote.contract?.status === "Signed";
  const identityLocked = !!quote.contract?.status && quote.contract.status === "Signed";
  const contactLocked = isLocked || !!quote.contract;
  const verification = quote.verification || EMPTY_VERIFICATION;
  const verificationComplete = isVerificationComplete(verification, quote.customerAddress);
  const canCompare = hasPerm("jobs_view");
  const StatusIcon = STATUS_ICONS[quote.status] || Target;

  /* ── Deposit method change handler ── */
  const handleDepositMethodChange = (method: string) => {
    setDepositMethod(method);
    const defaults = getDepositDefaults(method);
    const total = computedTotal || quote.totalEstimate;
    if (method === "Financing") {
      setDepositAmount("0");
      setDepositNotes(defaults.notes);
    } else {
      const halfAmount = Math.round(total * 0.5);
      setDepositAmount(String(halfAmount));
      setDepositNotes(defaults.notes);
    }
  };

  /* ── Lifecycle actions ── */
  const handleMarkPresented = () => {
    updateQuote({ presentedAt: new Date().toISOString() });
    toast.success("Opportunity marked as presented to homeowner");
  };

  const handleMarkWon = () => {
    setConvertDialogOpen(true);
  };

  const deriveSpecArchetype = useCallback((projectType: string): SpecArchetype | undefined => {
    const projectTypeStr = String(projectType || "").toLowerCase();
    if (projectTypeStr.includes("tub to shower") || projectTypeStr.includes("tub-to-shower")) return "TTS-STD";
    if (projectTypeStr.includes("shower remodel")) return "SHWR-RPL";
    if (projectTypeStr.includes("shower replacement")) return "SHWR-RPL";
    if (projectTypeStr.includes("walk-in tub") || projectTypeStr.includes("walk in tub")) return "WALK-IN-TUB";
    if (projectTypeStr.includes("full bathroom") || projectTypeStr.includes("full bath")) return "FULL-BATH";
    if (projectTypeStr.includes("tub replacement")) return "TUB-RPL";
    if (projectTypeStr.includes("floor")) return "FLOORING";
    return undefined;
  }, []);

  const deriveProjectTypeFallback = useCallback((cfg?: ProjectConfiguration): string => {
    if (!cfg) return "General";
    const explicit = String(cfg.autoProjectType || "").trim();
    if (explicit) return explicit;
    const scope = (cfg.bathroomScopes ?? []).map((s) => String(s).toLowerCase());
    if (scope.some((s) => s.includes("full bathroom"))) return "Full Bathroom Remodel";
    if (scope.some((s) => s.includes("tub to shower") || s.includes("tub-to-shower"))) return "Tub to Shower Conversion";
    const structural = cfg.structuralChanges;
    if (structural?.movingDrain || structural?.movingPlumbing) return "Tub to Shower Conversion";
    if (structural?.removingWalls || structural?.addingWalls || structural?.subfloorMod) return "Full Bathroom Remodel";
    if ((cfg.selectedVerticals ?? []).includes("Bathroom Project")) return "Shower Remodel";
    return "General";
  }, []);

  const buildConversionPreflight = useCallback(() => {
    const effectiveSkuCatalog = skuCatalog;
    const effectiveMappingRules = mappingRules;
    const config = quote.projectConfig;
    const customQuoteItems = config?.customQuoteItems ?? [];
    const normalize = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
    const catalogBySku = new Map(effectiveSkuCatalog.map((row) => [normalize(row.sku), row]));
    const catalogByName = new Map(effectiveSkuCatalog.map((row) => [normalize(row.itemName), row]));
    const unresolvedCustomItems = customQuoteItems.filter((ci) => {
      const label = String(ci.itemName ?? "").trim();
      if (!label) return true;
      const key = normalize(label);
      return !catalogBySku.has(key) && !catalogByName.has(key);
    });
    const materialAudit = config
      ? generateMaterialListAudit(config, effectiveMappingRules, effectiveSkuCatalog)
      : { requirements: [], missingCatalogSkus: [] };
    const materialRequirements = materialAudit.requirements;
    const derivedFields = config ? deriveJobFields(config) : { projectType: "General", scopeSummary: "" };
    const normalizedProjectType =
      String(derivedFields.projectType || "").trim().toLowerCase() === "general"
        ? deriveProjectTypeFallback(config)
        : derivedFields.projectType;
    const specArchetype = deriveSpecArchetype(normalizedProjectType);
    const canonicalSpec = specArchetype ? getCanonicalSpecForArchetype(specArchetype) : undefined;
    const reasons: string[] = [];
    if (skuCatalog.length === 0) reasons.push("Catalog SKUs are not loaded from DB");
    if (mappingRules.length === 0) reasons.push("SKU mapping rules are not configured in DB");
    if (materialAudit.missingCatalogSkus.length > 0) reasons.push("Mapping rules reference SKU(s) missing from Product Registry");
    if (unresolvedCustomItems.length > 0) reasons.push("Custom item(s) are not mapped to Product Registry/SKU");
    if (!canonicalSpec) reasons.push("No canonical JobSpec exists for this project type");
    return {
      reasons,
      canConvert: reasons.length === 0,
      unresolvedCustomItems,
      missingCatalogSkus: materialAudit.missingCatalogSkus,
      materialRequirements,
      canonicalSpec,
      derivedFields: { ...derivedFields, projectType: normalizedProjectType },
      specArchetype,
    };
  }, [deriveProjectTypeFallback, deriveSpecArchetype, mappingRules, quote.projectConfig, skuCatalog]);

  const conversionPreflight = useMemo(() => buildConversionPreflight(), [buildConversionPreflight]);

  const handleConfirmWon = async (contractOverride?: { amount: number; method: string; date: string; notes: string; contractDocument?: ContractDocument }) => {
    let depAmt: number;
    let method: string;
    let date: string;
    let notes: string;

    if (contractOverride) {
      // Called automatically after contract signing — deposit values come from the contract
      depAmt = contractOverride.amount;
      method = contractOverride.method;
      date = contractOverride.date;
      notes = contractOverride.notes;
    } else {
      depAmt = parseFloat(depositAmount);
      if (isNaN(depAmt) || depAmt < 0) {
        toast.error("Please enter a valid deposit amount (0 or more)");
        return;
      }
      if (!depositMethod) {
        toast.error("Please select a payment method");
        return;
      }
      if (!isDepositValid(depositMethod, depAmt)) {
        toast.error(`${depositMethod} requires a deposit greater than $0. Only Financing allows $0 down.`);
        return;
      }
      method = depositMethod;
      date = depositDate;
      notes = depositNotes;
    }

    const deposit: DepositInfo = {
      amount: depAmt,
      method,
      date,
      notes,
    };
    const now = new Date().toISOString();
    const nextJobNum = jobs.length + 1;
    const jobId = `JOB-${String(nextJobNum).padStart(3, "0")}`;

    // Resolve customerId — required by JobService.create.
    // If the quote already carries it (DB-loaded), use it directly.
    // Otherwise try to find or create the customer from the quote's contact info
    // so that jobs can be created even for quotes with a missing customer_id FK.
    let resolvedCustomerId: string | undefined = (quote as any).customerId as string | undefined;
    if (!resolvedCustomerId && initialQuote) {
      // Search for existing customer by email or phone before creating a new one.
      const email = quote.customerEmail?.trim();
      const phone = quote.customerPhone?.trim();
      let existingId: string | undefined;
      try {
        if (email) {
          const { data: found, error: findErr } = await CustomerService.getByField("email", email);
          if (findErr) throw findErr;
          existingId = (found as any)?.[0]?.id;
        }
        if (!existingId && phone) {
          const { data: found, error: findErr } = await CustomerService.getByField("phone", phone);
          if (findErr) throw findErr;
          existingId = (found as any)?.[0]?.id;
        }
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 403) {
          toast.error("Cannot access customers (permission denied). Contact your admin to fix customer access.");
        } else {
          console.error("[handleConfirmWon] Customer lookup failed:", err);
          toast.error("Failed to look up customer. Please try again.");
        }
        return;
      }
      if (existingId) {
        resolvedCustomerId = existingId;
      } else {
        const nameParts = (quote.customerName || "").trim().split(/\s+/);
        try {
          const { data: createdCust, error: createErr } = await CustomerService.create({
            first_name: nameParts[0] || "Customer",
            last_name: nameParts.slice(1).join(" ") || undefined,
            email: email || undefined,
            phone: phone || undefined,
            street_address: quote.customerAddress || undefined,
          });
          if (createErr) throw createErr;
          resolvedCustomerId = (createdCust as any)?.id as string | undefined;
        } catch (err) {
          const status = (err as { status?: number })?.status;
          if (status === 403) {
            toast.error("Cannot create customer (permission denied). Contact your admin to fix customer access.");
          } else {
            console.error("[handleConfirmWon] Customer creation failed:", err);
            toast.error("Failed to create customer record. Please try again.");
          }
          return;
        }
      }
      // Back-fill customer_id on the quote row so future operations work.
      if (resolvedCustomerId) {
        await onPersistUpdate!(id!, { customerId: resolvedCustomerId } as any).catch(() => {});
      }
    }

    const preflight = buildConversionPreflight();
    const config = quote.projectConfig;
    // Auto-flow (contract signing) must not be blocked by catalog/spec missing data —
    // the job is still created; materials may be incomplete until catalog loads.
    if (!contractOverride && !preflight.canConvert) {
      toast.error(`Cannot create job: ${preflight.reasons[0]}.`);
      return;
    }
    const effectiveSkuCatalog = skuCatalog;
    const materialRequirements = preflight.materialRequirements;
    const materialLineItems = toMaterialLineItems(materialRequirements);
    const normalizeKey = (value: string) => String(value || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
    const optionById = new Map(allOptions.map((o) => [String(o.id), o] as const));
    const optionByName = (name: string, category?: string) => {
      const target = String(name || "").trim().toLowerCase();
      if (!target) return undefined;
      return allOptions.find((o) =>
        (!category || o.category === category) &&
        (o.displayName.trim().toLowerCase() === target || o.displayName.trim().toLowerCase().includes(target) || target.includes(o.displayName.trim().toLowerCase()))
      );
    };
    const fallbackRows: typeof materialLineItems = [];
    const pushFallback = (rawName: string | undefined, skuHint: string | undefined, qty = 1) => {
      const name = String(rawName || "").trim();
      if (!name) return;
      if (isUuidLike(name)) return;
      // Don't create a fallback row if a catalog entry already covers this material.
      // Check both by normalized SKU hint and by name prefix (e.g. "Sentrel" matches
      // "Sentrel Standard Wall Panel Set" already present in materialLineItems).
      const nameNorm = normalizeKey(name);
      const skuRaw = String(skuHint || "").trim();
      // Strip UUID-like skuHints — they must not become the job_materials SKU.
      const sku = (skuRaw && !isUuidLike(skuRaw)) ? skuRaw : "";
      const skuNorm = normalizeKey(sku);
      const alreadyCovered = materialLineItems.some((m) => {
        const mn = normalizeKey(m.itemName);
        const ms = normalizeKey(m.sku);
        return ms === skuNorm || mn === nameNorm || mn.startsWith(nameNorm) || nameNorm.startsWith(mn);
      });
      if (alreadyCovered) return;
      fallbackRows.push({
        sku: sku || name,
        itemName: name,
        quantityRequired: Math.max(1, Number(qty || 1)),
        quantityAllocated: 0,
        quantityStaged: 0,
        quantityInstalled: 0,
        missingItems: Math.max(1, Number(qty || 1)),
        unitCost: 0,
        unitPrice: 0,
        fieldPurchase: false,
        inventoryAssignment: "Stock" as const,
        lifecycleStage: "Ordered" as const,
        inventoryStatus: "Low Stock",
      });
};
     const projectConfig = currentConfig || quote.projectConfig;
     if (projectConfig) {
       const wallOpt = optionById.get(String(projectConfig.selectedWallSystem || ""));
       const baseOpt = optionById.get(String(projectConfig.selectedBase || ""));
       const glassOpt = optionById.get(String(projectConfig.selectedGlass || ""));
       const doorOpt = optionById.get(String(projectConfig.selectedDoor || ""));
       const fixtureOpt = optionById.get(String(projectConfig.selectedFixture || ""));
       pushFallback(wallOpt?.displayName || projectConfig.selectedWallSystem || projectConfig.primaryMaterial || projectConfig.bathroomMaterials?.[0], wallOpt?.skuPlaceholder);
       pushFallback(baseOpt?.displayName || projectConfig.selectedBase || projectConfig.baseType, baseOpt?.skuPlaceholder);
       const enclosureOpt = optionByName(projectConfig.enclosureType || "", "enclosure");
       pushFallback(enclosureOpt?.displayName || projectConfig.enclosureType || glassOpt?.displayName, enclosureOpt?.skuPlaceholder || glassOpt?.skuPlaceholder);
       pushFallback(fixtureOpt?.displayName || projectConfig.selectedFixture, fixtureOpt?.skuPlaceholder);
       (projectConfig.selectedGrabBars || []).forEach((id) => {
         const opt = optionById.get(String(id));
         pushFallback(opt?.displayName || String(id), opt?.skuPlaceholder);
       });
       (projectConfig.selectedAddOns || []).forEach((id) => {
         const opt = optionById.get(String(id));
         pushFallback(opt?.displayName || String(id), opt?.skuPlaceholder);
       });
     }
    const mergedByKey = new Map<string, (typeof materialLineItems)[number]>();
    [...materialLineItems, ...fallbackRows].forEach((row) => {
      const key = normalizeKey(row.sku) || normalizeKey(row.itemName);
      const existing = mergedByKey.get(key);
      if (!existing) {
        mergedByKey.set(key, { ...row });
        return;
      }
      existing.quantityRequired = Math.max(existing.quantityRequired, row.quantityRequired);
      existing.missingItems = Math.max(existing.missingItems, row.missingItems);
      if (!normalizeKey(existing.sku) && normalizeKey(row.sku)) existing.sku = row.sku;
      if (!existing.itemName || isUuidLike(existing.itemName)) existing.itemName = row.itemName;
    });
    const mergedMaterialLineItems = Array.from(mergedByKey.values());
    if (fallbackRows.length > 0) {
      toast.warning("Some materials are fallback/unmapped", {
        description: `${fallbackRows.length} fallback material(s) were generated without cost/price. Map them in Product Registry before final ordering.`,
      });
    }
    const materialsReadiness = computeMaterialsReadiness(materialRequirements);
    const vendorCounts = new Map<string, number>();
    for (const req of materialRequirements) {
      const skuRow = effectiveSkuCatalog.find((sku) => sku.sku === req.sku);
      const vendorName = String(skuRow?.primaryVendor?.name ?? "").trim();
      if (!vendorName) continue;
      vendorCounts.set(vendorName, (vendorCounts.get(vendorName) ?? 0) + req.quantityRequired);
    }
    const selectedVendor = [...vendorCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
const materialsStatusMap: Record<string, MaterialsStatus> = {
       "Ready": "Ready",
       "Partially Available": "Missing Items",
       "Not Available": "Not Ready",
     };

      const derivedFields = preflight.derivedFields;

       // Bind a JobSpec snapshot that powers the Materials/BOM tab and ordering alignment.
       // The BOM is sourced from the actual project configuration (materialRequirements),
       // not from the generic spec registry. The canonical spec provides the phases/checklist.
       const actualBomItems = materialRequirements.map(req => ({
         sku: req.sku,
         name: req.itemName,
         quantity: req.quantityRequired,
         cost: req.unitCost && req.unitCost > 0 ? req.unitCost : undefined,
         price: req.unitPrice && req.unitPrice > 0 ? req.unitPrice : undefined,
       }));
      const canonicalSpec = preflight.canonicalSpec;

      // Build the spec to bind only when the quote has real configured BOM rows.
      // Do not fall back to the canonical spec BOM, or jobs with no actual
      // materials will display a generated generic BOM.
      if (!canonicalSpec) {
        if (!contractOverride) {
          toast.error("Cannot create job: no canonical JobSpec exists for this project type.");
          return;
        }
      }
      const specToBind: JobSpec | undefined = canonicalSpec && actualBomItems.length > 0 ? {
        ...canonicalSpec,
        billOfMaterials: actualBomItems,
      } : undefined;

      const boundJobSpec = specToBind ? bindSpec(specToBind) : undefined;

      const newJob = {
      id: jobId,
      customerName: quote.customerName,
      address: parseAddressPartsFromText(quote.customerAddress),
      phone: quote.customerPhone,
      email: quote.customerEmail,
      secondaryContactName: verification.hasSecondaryContact ? `${verification.secondaryFirstName} ${verification.secondaryLastName}`.trim() : "",
      secondaryContactPhone: verification.hasSecondaryContact ? verification.secondaryPhone : "",
      secondaryContactEmail: verification.hasSecondaryContact ? verification.secondaryEmail : "",
      projectType: derivedFields.projectType,
      assignedCrew: "",
      assignmentType: "Unassigned" as const,
      scheduledInstallDate: "",
      permitRequired: permitRequiredOverride,
      permitStatus: permitRequiredOverride ? "Submitted" as const : "Not Required" as const,
      permitDetail: permitRequiredOverride ? {
        permitType: "Residential Remodel",
        municipality: "",
        submittedDate: "",
        detailedStage: "Permit Required",
        lastUpdate: now,
        permitNotes: permitAssessment.reasons.join(" • "),
        assignedCoordinator: "",
        blockers: "",
        targetApprovalDate: "",
      } : undefined,
      orderStatus: "Not Ordered" as const,
      materialsStatus: materialsStatusMap[materialsReadiness] || "Not Ready",
      dispatchStatus: "Not Ready" as const,
      contractorAssignmentStatus: "Unassigned",
      currentStage: "Sale Closed" as const,
      notes: quote.notes,
      issueFlag: false,
      scopeSummary: derivedFields.scopeSummary,
      payoutAmount: 0,
      assignedSubcontractor: "",
      targetInstallDate: "",
      activityHistory: [
        { id: "conv-1", date: now.slice(0, 10), action: `Job created from Quote ${quote.id} (Won)`, user: "System", noteType: "system" as const },
        ...(mergedMaterialLineItems.length > 0 ? [{ id: "conv-2", date: now.slice(0, 10), action: `Material list generated: ${mergedMaterialLineItems.length} items, status: ${materialsReadiness}`, user: "System", noteType: "system" as const }] : []),
      ],
      photos: [],
      qaSubmitted: false,
      branch: "",
      region: "",
      salesRep: quote.assignedRep || quote.createdBy,
      materialReadiness: materialsReadiness === "Ready" ? "Ready" as const : "Not Ordered" as const,
      materialReadinessNotes: materialsReadiness !== "Ready" ? `${materialLineItems.filter(m => m.missingItems > 0).length} items need procurement` : "",
      missingItemsList: mergedMaterialLineItems.filter(m => m.missingItems > 0).map(m => m.itemName).join(", "),
      procurementStatus: "Not Ordered" as const,
      warehouseStatus: "Not Received" as const,
      selectedVendor,
      selectedVendorType: selectedVendor ? "Primary" : "",
      closeoutStatus: "Not Started" as const,
      finalDocumentationComplete: false,
      customerSignoff: false,
      closeoutNotes: "",
      projectConfig: quote.projectConfig,
      boundJobSpec,
      materialsList: mergedMaterialLineItems,
      rescissionStatus: "In Rescission" as const,
      rescissionEndsAt: calculateRescissionEnd(now),
      convertedFromQuoteId: quote.id,
      // Pass customerId directly so JobService.create doesn't need a DB lookup
      // to resolve the customer FK (avoids failure when quote.customer_id is NULL).
      ...(resolvedCustomerId ? { customerId: resolvedCustomerId } : {}),
      contractDocument: contractOverride?.contractDocument ?? quote.contract,
      marketing: quote.leadSource ? { leadSource: quote.leadSource, campaignName: "", aggregatorPlatform: "", callCenterRep: "", appointmentDate: "", saleOutcome: "Won" } : undefined,
      // CANONICAL: seed financials.contractValue from the sold quote total.
      // See mem://finance/canonical-pricing.
      financials: (computedTotal || quote.totalEstimate) > 0 ? {
        contractValue: computedTotal || quote.totalEstimate,
        estimatedMaterialCost: 0,
        estimatedLaborCost: 0,
        // Subcontractor payout is set when an offer is accepted — not at job creation.
        subcontractorPayout: 0,
        marginEstimate: 0,
        paymentStatus: "Not Invoiced" as const,
        payoutStatus: "Not Submitted" as const,
        invoices: [],
        payments: [],
      } : undefined,
    };
    if (initialQuote && onPersistUpdate) {
      // DB-wired: persist via JobService
      const { data: createdJob, error: jobError } = await JobService.create(newJob as any);
      if (jobError || !createdJob) {
        const reason =
          jobError instanceof Error
            ? jobError.message
            : (jobError as { message?: string })?.message ?? "unknown error";
        console.error("[handleConfirmWon] Job creation failed:", reason);
        toast.error(`Failed to create job: ${reason}. Please try again.`);
        return;
      }
      const realJobId = createdJob.id;

      // Persist materials snapshot (same as AppointmentFlow)
      if (mergedMaterialLineItems.length > 0) {
        await replaceJobMaterialsSnapshot({
          id: createdJob.id,
          jobNumber: createdJob.jobNumber,
          customerName: createdJob.customerName,
          currentStage: createdJob.currentStage,
          materialsList: mergedMaterialLineItems,
        }).catch((err: unknown) => console.warn("[handleConfirmWon] Materials snapshot failed:", err));
      }

      const wonUpdates: Partial<Quote> = {
        status: "Closed Won" as QuoteStatus,
        stage: "Closed Won" as any,
        convertedJobId: realJobId,
        linkedJobId: realJobId,
        wonAt: now,
        lockedAt: now,
        lockedBy: role,
        totalEstimate: computedTotal || quote.totalEstimate,
        deposit,
      };

      // Update quote in DB (status + linked_job_id)
      try {
        await onPersistUpdate(id!, wonUpdates);
      } catch (err) {
        console.error("[handleConfirmWon] Quote status update failed:", err);
        toast.error("Job created but opportunity status failed to save. Please refresh the page.");
        return;
      }

      // Update lead stage in DB — this is what QuoteService.getAll() reads to show status in the list
      const quoteLeadId = (quote as any).leadId as string | undefined;
      if (quoteLeadId) {
        await LeadService.update(quoteLeadId, {
          stage: "Closed Won",
          linked_quote_id: id!,
          linked_job_id: realJobId,
        }).catch((err) => console.warn("[handleConfirmWon] Lead stage update failed:", err));
      }

      // Sync AppDataContext (jobs list + opportunities list)
      setJobs((prev) => [...prev, createdJob]);
      setLocalQuote((prev) => prev ? { ...prev, ...wonUpdates, updatedAt: now.slice(0, 10) } : prev);
      setQuotes((prev) =>
        prev.map((q) => q.id === id ? { ...q, ...wonUpdates, updatedAt: now.slice(0, 10) } : q)
      );

      setConvertDialogOpen(false);
      toast.success(`Opportunity won! Job created.`);
      navigate(`/jobs/${realJobId}`);
    } else {
      // Classic: seed-data path (unchanged)
      setJobs((prev) => [...prev, newJob as any]);

      if (mergedMaterialLineItems.length > 0) {
        const draftPO = generateDraftPO(jobId, mergedMaterialLineItems);
        if (draftPO) {
          setPurchaseOrders((prev) => [...prev, draftPO]);
        }
        setStagingRecords((prev) => [...prev, createStagingRecord(jobId, mergedMaterialLineItems)]);
        setLoadOutChecklists((prev) => [...prev, createLoadOutChecklist(jobId)]);
      }

      updateQuote({
        status: "Closed Won",
        convertedJobId: jobId,
        wonAt: now,
        lockedAt: now,
        lockedBy: role,
        totalEstimate: computedTotal || quote.totalEstimate,
        deposit,
      });
      setConvertDialogOpen(false);
      toast.success(`Opportunity won! Job ${jobId} created.${mergedMaterialLineItems.filter(m => m.missingItems > 0).length > 0 ? " Draft PO generated for missing materials." : ""}`);
      navigate(`/jobs/${jobId}`);
    }
  };

  const handleMarkLost = () => {
    setLostDialogOpen(true);
  };

  const handleConfirmLost = () => {
    updateQuote({
      status: "Closed Lost",
      stage: "Closed Lost",
      lostReason,
      lostAt: new Date().toISOString(),
      lockedAt: new Date().toISOString(),
      lockedBy: role,
    });
    setLostDialogOpen(false);
    setLostReason("");
    toast("Opportunity marked as Closed Lost");
  };

  // ── Stage transition handlers (Review & Present tab) ──
  const handleCompleteConsultation = () => {
    updateQuote({ stage: "Appointment Completed", status: "Appointment Completed" });
    toast.success("Consultation marked complete.");
  };

  const handleConfirmFollowUp = () => {
    updateQuote({
      stage: "Follow-Up Needed",
      status: "Follow-Up Needed",
      dispositionReason: followUpDispositionReason,
      followUpType: followUpType as "rep" | "manager",
      followUpDueDate,
      dispositionNotes: followUpNotes,
    });
    setFollowUpTransitionOpen(false);
    setFollowUpDispositionReason("");
    setFollowUpType("rep");
    setFollowUpDueDate(getDefaultFollowUpDate());
    setFollowUpNotes("");
    toast.success("Follow-up scheduled.");
  };

  const handleReopenOpportunity = () => {
    updateQuote({ stage: "Appointment Completed", status: "Appointment Completed" });
    toast.success("Opportunity reopened.");
  };

  const handleContractCancel = (reason: CancelReason, customReason?: string) => {
    if (!quote.contract) return;
    const now = new Date().toISOString();
    const cancelledContract: ContractDocument = {
      ...quote.contract,
      status: "Cancelled",
      cancellation: {
        reason,
        customReason,
        cancelledAt: now,
        cancelledBy: role,
      },
    };
    updateQuote({
      contract: cancelledContract,
      status: "Appointment Completed" as QuoteStatus,
    });
    toast.success("Contract cancelled. Opportunity reopened for follow-up.");
  };

   const handleConfigSave = (config: ProjectConfiguration) => {
     setCurrentConfig(config);  // Update current draft for delta tracking
     const total = parseFloat(config.basePrice) || 0;
     const manualAdj = parseFloat(config.manualAdjustment) || 0;
     const updates: Record<string, any> = {
       projectConfig: config,
       totalEstimate: total > 0 ? total : quote.totalEstimate,
     };
     const pStatus = computePricingStatus(total, quote.pricingGuardrails);
     updates.pricingStatus = pStatus;
     if (manualAdj !== 0) {
       const calcPrice = total - manualAdj;
       updates.pricingOverride = {
         originalCalculatedPrice: calcPrice,
         adjustedPrice: total,
         variance: manualAdj,
         adjustmentAmount: manualAdj,
         reason: config.manualAdjustmentNotes?.split("||")[0] || "",
         notes: config.manualAdjustmentNotes?.split("||")[1] || "",
         appliedBy: role,
         appliedAt: new Date().toISOString(),
         requiresReview: pStatus === "Below Floor",
       };
     }
     updateQuote(updates);
     toast.success("Configuration saved.");
   };

  /* ── Wizard step validation ── */
  const isStepComplete = (stepIdx: number): boolean => {
    switch (stepIdx) {
      case 0: return verificationComplete;
      case 1: return !!(quote.projectConfig && (quote.projectConfig.selectedVerticals?.length ?? 0) > 0);
      case 2: return computedTotal > 0;
      case 3: return true;
      default: return false;
    }
  };

  const canProceedToStep = (stepIdx: number): boolean => {
    // Can always go back. For going forward, previous steps should be complete.
    for (let i = 0; i < stepIdx; i++) {
      if (!isStepComplete(i)) return false;
    }
    return true;
  };

  const handleWizardNext = () => {
    if (wizardStep < WIZARD_STEPS.length - 1) {
      setWizardStep(wizardStep + 1);
    }
  };

  const handleWizardBack = () => {
    if (wizardStep > 0) setWizardStep(wizardStep - 1);
  };

  /* ── Render wizard step content ── */
  const renderWizardStep = () => {
    switch (WIZARD_STEPS[wizardStep].id) {
      case "customer":
        return (
          <div className="space-y-4">
            <CustomerPropertyVerification
              verification={verification}
              address={quote.customerAddress}
              onVerificationChange={(v) => {
                const fullName = `${v.primaryFirstName} ${v.primaryLastName}`.trim();
                updateQuote({
                  verification: v,
                  customerName: fullName || quote.customerName,
                  customerPhone: v.primaryPhone || quote.customerPhone,
                  customerEmail: v.primaryEmail || quote.customerEmail,
                });
              }}
              onAddressChange={(a) => updateQuote({ customerAddress: a })}
              locked={identityLocked}
              prefillData={prefillData}
            />
            {/* Quote Details (name/lead source) intentionally omitted during the
                appointment flow — surfaced later in Pricing/Review. Keeps the
                consultation focused on capturing the customer + property. */}

            {/* Step footer — gated Next → Project Scope */}
            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-[11px] text-muted-foreground">
                {verificationComplete
                  ? "All required fields complete. Ready to configure the build."
                  : "Complete customer & property verification to continue."}
              </p>
              <Button
                size="sm"
                className="gap-1.5"
                disabled={!verificationComplete}
                onClick={handleWizardNext}
              >
                Next: Project Scope <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        );

       case "scope":
         return (
           <div className="rounded-lg border border-border bg-card overflow-hidden p-4 space-y-4">
             <ProjectConfigurator
               open={true}
               onOpenChange={() => {}}
               job={{ id: quote.id, customerName: quote.customerName, payoutAmount: quote.totalEstimate } as any}
               config={currentConfig || quote.projectConfig || DEFAULT_CONFIG}
               pricingGuardrails={quote.pricingGuardrails}
               onSave={handleConfigSave}
               onDraftChange={setCurrentConfig}
               renderMode="inline"
               onWizardBack={handleWizardBack}
               onWizardNext={handleWizardNext}
             />
           </div>
         );

      case "pricing":
        return (
          <div className="space-y-5">
            {/* Pricing Breakdown — restricted for crew roles */}
            {fin$.isCrew ? (
              <Card className="shadow-none">
                <CardContent className="p-6 text-center">
                  <ShieldCheck className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm font-medium">Pricing details restricted</p>
                  <p className="text-xs text-muted-foreground mt-1">Your role does not have access to pricing breakdowns</p>
                </CardContent>
              </Card>
            ) : priceBreakdown && priceBreakdown.finalTotal > 0 ? (
              <Card className="shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Pricing Breakdown</h2>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-muted-foreground">{`Base System (${priceBreakdown.systemType})`}</span>
                      <span className="font-medium shrink-0">${priceBreakdown.baseSystemPrice.toLocaleString()}</span>
                    </div>
                    {priceBreakdown.dimensionAdjustment > 0 && (
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-muted-foreground">{`Dimension Adj. (${priceBreakdown.dimensions})`}</span>
                        <span className="font-medium shrink-0">+${priceBreakdown.dimensionAdjustment.toLocaleString()}</span>
                      </div>
                    )}
                    {priceBreakdown.materialAdjustment > 0 && (
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-muted-foreground">{`Material (${priceBreakdown.material})`}</span>
                        <span className="font-medium shrink-0">+${priceBreakdown.materialAdjustment.toLocaleString()}</span>
                      </div>
                    )}
                    {priceBreakdown.componentDetails.length > 0 && (
                      <>
                        <div className="border-t pt-1.5 mt-1.5">
                          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Components</span>
                        </div>
                        {priceBreakdown.componentDetails.map((c, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 text-xs pl-2">
                            <span className="text-muted-foreground">{c.name}</span>
                            <span className="font-medium shrink-0">
                              {c.amount === 0
                                ? (c.pricingLabel || "Included")
                                : `${c.amount > 0 ? "+" : ""}$${c.amount.toLocaleString()}`}
                            </span>
                          </div>
                        ))}
                      </>
                     )}
                     {/* Material Changes Delta */}
                     {materialDeltas && materialDeltas.length > 0 && (
                       <>
                         <div className="border-t pt-1.5 mt-1.5">
                           <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Material Changes</span>
                         </div>
                         {materialDeltas.map((delta, i) => (
                           <div key={i} className="flex items-center justify-between gap-2 text-xs pl-2">
                             <span className="text-muted-foreground">{delta.name}</span>
                             <span className={`font-medium shrink-0 ${delta.deltaAmount < 0 ? "text-green-600" : delta.deltaAmount > 0 ? "text-red-600" : ""}`}>
                               {delta.deltaAmount > 0 ? "+" : ""}${delta.deltaAmount.toLocaleString()}
                             </span>
                           </div>
                         ))}
                       </>
                     )}
                     {priceBreakdown.complexityAdjustment > 0 && (
                      <div className="flex justify-between text-xs border-t pt-1.5 mt-1.5">
                        <span className="text-muted-foreground">Complexity</span>
                        <span className="font-medium">+${priceBreakdown.complexityAdjustment.toLocaleString()}</span>
                      </div>
                    )}
                    {priceBreakdown.manualAdjustment !== 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Manual Adj.{priceBreakdown.manualAdjustmentNotes ? ` (${priceBreakdown.manualAdjustmentNotes})` : ""}</span>
                        <span className={`font-medium ${priceBreakdown.manualAdjustment < 0 ? "text-destructive" : ""}`}>
                          {priceBreakdown.manualAdjustment > 0 ? "+" : ""}${priceBreakdown.manualAdjustment.toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 mt-2 border-t">
                      <span className="text-sm font-bold">Final Total</span>
                      <span className="text-lg font-bold text-primary">${priceBreakdown.finalTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-none">
                <CardContent className="p-8 text-center">
                  <DollarSign className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No pricing yet</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">Go back to Project Scope and complete the configuration</p>
                  <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => setWizardStep(1)}>
                    <ArrowLeft className="h-3 w-3" /> Back to Configuration
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Pricing Control Panel */}
            {canManageGuardrails && priceBreakdown && (
              <Card className={`shadow-none ${pricingStatus === "Below Floor" ? "border-destructive/40" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                      <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Pricing Control</h2>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[9px] font-bold ${statusStyle.color}`}>
                      {pricingStatus === "Below Floor" && <AlertTriangle className="h-2.5 w-2.5" />}
                      {statusStyle.label}
                    </span>
                  </div>
                  <div className="space-y-1.5 text-xs mb-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Calculated Price</span>
                      <span className="font-medium">${priceBreakdown.calculatedPrice.toLocaleString()}</span>
                    </div>
                    {priceBreakdown.manualAdjustment !== 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Adjustment</span>
                          <span className={`font-medium ${priceBreakdown.manualAdjustment < 0 ? "text-destructive" : ""}`}>
                            {priceBreakdown.manualAdjustment > 0 ? "+" : ""}${priceBreakdown.manualAdjustment.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Adjusted Price</span>
                          <span className="font-bold">${priceBreakdown.finalTotal.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Variance</span>
                          <span className={`font-medium ${priceBreakdown.manualAdjustment < 0 ? "text-destructive" : "text-success"}`}>
                            {priceBreakdown.manualAdjustment > 0 ? "+" : ""}${priceBreakdown.manualAdjustment.toLocaleString()}
                            {" "}({priceBreakdown.calculatedPrice > 0 ? ((priceBreakdown.manualAdjustment / priceBreakdown.calculatedPrice) * 100).toFixed(1) : "0"}%)
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {!isLocked && (
                    <div className="pt-3 border-t">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Margin Guardrails (Internal)</p>
                      <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[9px] text-muted-foreground">Target $</Label>
                            <Input type="number" className="h-7 text-xs mt-0.5" placeholder="0"
                              value={quote.pricingGuardrails?.targetPrice || ""}
                              onChange={(e) => updateQuote({
                                pricingGuardrails: {
                                  ...(quote.pricingGuardrails || { targetPrice: 0, floorPrice: 0, premiumThreshold: 0 }),
                                  targetPrice: parseFloat(e.target.value) || 0,
                                }
                              })} />
                          </div>
                          <div>
                            <Label className="text-[9px] text-muted-foreground">Floor $</Label>
                            <Input type="number" className="h-7 text-xs mt-0.5" placeholder="0"
                              value={quote.pricingGuardrails?.floorPrice || ""}
                              onChange={(e) => updateQuote({
                                pricingGuardrails: {
                                  ...(quote.pricingGuardrails || { targetPrice: 0, floorPrice: 0, premiumThreshold: 0 }),
                                  floorPrice: parseFloat(e.target.value) || 0,
                                }
                              })} />
                          </div>
                          <div>
                            <Label className="text-[9px] text-muted-foreground">Premium $</Label>
                            <Input type="number" className="h-7 text-xs mt-0.5" placeholder="Optional"
                              value={quote.pricingGuardrails?.premiumThreshold || ""}
                              onChange={(e) => updateQuote({
                                pricingGuardrails: {
                                  ...(quote.pricingGuardrails || { targetPrice: 0, floorPrice: 0, premiumThreshold: 0 }),
                                  premiumThreshold: parseFloat(e.target.value) || 0,
                                }
                              })} />
                          </div>
                        </div>
                        <Button variant="outline" size="sm" className="h-6 text-[10px] w-full"
                          onClick={() => {
                            const calc = priceBreakdown.calculatedPrice;
                            if (calc > 0) {
                              updateQuote({
                                pricingGuardrails: {
                                  targetPrice: calc,
                                  floorPrice: Math.round(calc * 0.85),
                                  premiumThreshold: Math.round(calc * 1.15),
                                }
                              });
                            }
                          }}>
                          Auto-set from calculated price (85%–115%)
                        </Button>
                      </div>
                    </div>
                  )}

                  {pricingStatus === "Below Floor" && (
                    <div className="mt-3 p-2 rounded bg-destructive/10 border border-destructive/20">
                      <p className="text-[10px] font-bold text-destructive flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Price is below minimum floor — requires manager review
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card className="shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Notes</h2>
                </div>
                <Textarea
                  value={quote.notes}
                  onChange={(e) => updateQuote({ notes: e.target.value })}
                  placeholder="Add notes about this quote…"
                  rows={3}
                  className="text-xs"
                />
              </CardContent>
            </Card>
          </div>
        );

      case "review":
        return (
          <div className="space-y-5">
            {/* Summary Card */}
            <Card className="shadow-none border-primary/20 bg-primary/5">
              <CardContent className="p-5">
                <h2 className="text-sm font-bold flex items-center gap-2 mb-4">
                  <Sparkles className="h-4 w-4 text-primary" /> Quote Summary
                </h2>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{quote.customerName}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="font-medium truncate max-w-[200px]">{quote.customerAddress}</span></div>
                  {quote.customerPhone && <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium">{quote.customerPhone}</span></div>}
                  {quote.customerEmail && <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{quote.customerEmail}</span></div>}
                  {verification.ownershipType && <div className="flex justify-between"><span className="text-muted-foreground">Ownership</span><span className="font-medium">{verification.ownershipType}</span></div>}
                  {verification.hoaInvolved && <div className="flex justify-between"><span className="text-muted-foreground">HOA</span><Badge variant="outline" className="text-[9px] h-4 text-warning border-warning/30">HOA Involved</Badge></div>}
                </div>
              </CardContent>
            </Card>

            {/* Site Documentation */}
            <Card className="shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 pb-1.5 mb-4 border-b">
                  <Camera className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Site Documentation</h2>
                  {sitePhotos.length > 0 && (
                    <Badge variant="secondary" className="text-[9px] ml-auto">{sitePhotos.length} photo{sitePhotos.length !== 1 ? "s" : ""}</Badge>
                  )}
                </div>

                {!isUuidLike(quote.id) ? (
                  <p className="text-xs text-muted-foreground italic">Photo upload is available once this quote is saved to the database.</p>
                ) : (
                <div className="space-y-5">
                  {PHOTO_CATEGORIES.map(cat => {
                    const catPhotos = sitePhotos.filter(p => p.category === cat.id);
                    const Icon = cat.icon;
                    return (
                      <div key={cat.id}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            <p className="text-xs font-medium text-foreground">{cat.label}</p>
                            {catPhotos.length > 0 && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1.5">{catPhotos.length}</Badge>
                            )}
                          </div>
                          {uploadingCategory === cat.id ? (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <RefreshCw className="h-3 w-3 animate-spin" /> Uploading…
                            </span>
                          ) : (
                            <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-primary hover:underline">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                className="hidden"
                                onChange={(e) => handlePhotoUpload(e, cat.id)}
                              />
                              <Plus className="h-3 w-3" /> Add
                            </label>
                          )}
                        </div>

                        {catPhotos.length > 0 ? (
                          <div className="space-y-2">
                            {catPhotos.map(photo => (
                              <div key={photo.id} className="relative group rounded-md overflow-hidden border border-border bg-muted/30">
                                <button
                                  type="button"
                                  className="w-full block focus:outline-none"
                                  onClick={() => setLightboxPhoto(photo)}
                                  aria-label="View full image"
                                >
                                  <StorageThumbnailImage
                                    src={photo.preview}
                                    bucket="job-documents"
                                    alt={photo.name}
                                    className="w-full object-cover"
                                    style={{ maxHeight: "280px" }}
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                    <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeSitePhoto(photo.id)}
                                  className="absolute top-2 right-2 h-6 w-6 rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow"
                                  aria-label="Remove photo"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                                <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1 pointer-events-none">
                                  <p className="text-[10px] text-white truncate">{photo.name}</p>
                                </div>
                              </div>
                            ))}
                            {/* Inline add button */}
                            <label className="cursor-pointer block">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                className="hidden"
                                onChange={(e) => handlePhotoUpload(e, cat.id)}
                              />
                              <div className="h-10 rounded-md border border-dashed border-border flex items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors">
                                <ImagePlus className="h-3.5 w-3.5" />
                                <span className="text-xs">Add another photo</span>
                              </div>
                            </label>
                          </div>
                        ) : (
                          <label className="cursor-pointer block">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              capture="environment"
                              className="hidden"
                              onChange={(e) => handlePhotoUpload(e, cat.id)}
                            />
                            <div className="border border-dashed border-border rounded-md h-16 flex items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors">
                              <ImagePlus className="h-4 w-4" />
                              <span className="text-xs">{cat.emptyLabel}</span>
                            </div>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>

            {/* Photo Lightbox */}
            <Dialog open={!!lightboxPhoto} onOpenChange={(open) => { if (!open) setLightboxPhoto(null); }}>
              <DialogContent className="max-w-[50vw] w-[50vw] p-0 overflow-hidden bg-black border-0 rounded-lg">
                {lightboxPhoto && (
                  <div className="flex flex-col">
                    {/* Top bar */}
                    <div className="flex items-center gap-3 px-4 py-3 pr-12 bg-black/80 shrink-0">
                      <p className="text-xs text-white/70 truncate flex-1">{lightboxPhoto.name}</p>
                      <a
                        href={lightboxPhoto.preview}
                        download={lightboxPhoto.name}
                        className="inline-flex items-center gap-1.5 text-xs text-white bg-white/20 hover:bg-white/30 transition-colors rounded px-2.5 py-1.5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="h-3.5 w-3.5" /> Download
                      </a>
                    </div>
                    {/* Full image — natural dimensions, no cropping */}
                    <img
                      src={lightboxPhoto.preview}
                      alt={lightboxPhoto.name}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Configuration Summary */}
            {configSummaryItems.length > 0 && (
              <Card className="shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                    <ClipboardList className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Build Configuration</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {configSummaryItems.map((item) => (
                      <div key={item.label} className="flex items-center justify-between gap-2 py-1.5">
                        <span className="text-xs text-muted-foreground shrink-0">{item.label}</span>
                        <span className="text-xs font-medium text-right truncate">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  {quote.projectConfig?.customQuoteItems && quote.projectConfig.customQuoteItems.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Custom Items</p>
                      {quote.projectConfig.customQuoteItems.map((ci) => (
                        <div key={ci.id} className="flex items-center justify-between py-1 text-xs">
                          <span className="font-medium">{ci.itemName}</span>
                          <Badge variant="outline" className="text-[9px]">Custom</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Pricing Summary */}
            {priceBreakdown && priceBreakdown.finalTotal > 0 && (
              <Card className="shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Pricing</h2>
                    </div>
                  </div>
                  {(() => {
                    const isSigned = quote.contract?.status === "Signed";
                    const subtotal = priceBreakdown.calculatedPrice;
                    const discount = priceBreakdown.manualAdjustment < 0 ? Math.abs(priceBreakdown.manualAdjustment) : 0;
                    const finalTotal = quote.contract?.contractTotal ?? priceBreakdown.finalTotal;
                    return (
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">Subtotal</span>
                          <span className="text-xs font-medium text-muted-foreground">${subtotal.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">Discount</span>
                          <span className={`text-xs font-medium ${discount > 0 ? "text-success" : "text-muted-foreground"}`}>
                            {discount > 0 ? `-$${discount.toLocaleString()}` : "$0"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 mt-1 border-t">
                          <span className="text-sm font-bold">Final Total</span>
                          <span className={`text-xl font-bold ${isSigned ? "text-success" : "text-primary"}`}>
                            ${finalTotal.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Permit Required */}
            {!fin$.isCrew && (
              <Card className="shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Permit</h2>
                    {permitRequiredOverride && (
                      <span className="ml-auto text-[9px] font-bold px-2 py-0.5 rounded-full bg-warning/15 text-warning">Required</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-foreground">Permit Required</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {permitAssessment.reasons.length > 0
                          ? `Auto-detected: ${permitAssessment.reasons.join(" • ")}`
                          : "Override if your jurisdiction requires a permit for this scope."}
                      </p>
                    </div>
                    <Switch
                      checked={permitRequiredOverride}
                      onCheckedChange={(checked) => {
                        setPermitRequiredOverride(checked);
                        if (quote.projectConfig) {
                          updateQuote({ projectConfig: { ...quote.projectConfig, permitRequired: checked } });
                        }
                      }}
                      disabled={isLocked}
                    />
                  </div>
                  {permitRequiredOverride && (
                    <div className="mt-3 p-2.5 rounded-lg bg-warning/5 border border-warning/20 text-[11px] text-warning font-medium flex items-start gap-2">
                      <Shield className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      This job will be routed to permitting after sale is confirmed.
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Contract Status */}
            {quote.contract && <ContractDocumentView contract={quote.contract} />}

            {/* Version History */}
            {quote.contract && (() => {
              const lineage = buildLineageFromRecords(
                quote.id,
                quote.projectConfig,
                computedTotal || quote.totalEstimate,
                quote.contract,
              );
              if (lineage.versions.length === 0) return null;
              return (
                <Card className="shadow-none">
                  <CardContent className="p-4">
                    <VersionTimeline lineage={lineage} />
                  </CardContent>
                </Card>
              );
            })()}

            {/* Stage Transition Panel */}
            {quote.stage !== "Closed Won" && (
              <Card className="shadow-none border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 pb-2 mb-3 border-b">
                    <Shield className="h-4 w-4 text-primary" />
                    <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Stage Transition</h2>
                  </div>
                  <div className="space-y-2">
                    {/* Appointment Scheduled → Complete Consultation */}
                    {quote.stage === "Appointment Scheduled" && (
                      <PermissionGate permission="leads_edit">
                        <Button size="sm" className="h-9 text-xs gap-1.5 w-full justify-between" onClick={handleCompleteConsultation}>
                          <span className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5" /> Complete Consultation</span>
                          <ChevronRight className="h-3 w-3 opacity-50" />
                        </Button>
                      </PermissionGate>
                    )}
                    {/* Appointment Completed → Won | Follow-Up */}
                    {quote.stage === "Appointment Completed" && (
                      <>
                        <div className="space-y-1">
                          <PermissionGate permission="leads_edit">
                            <Button size="sm" className="h-9 text-xs gap-1.5 w-full justify-between bg-success hover:bg-success text-white disabled:opacity-50" onClick={() => setConvertDialogOpen(true)} disabled={quote.contract?.status !== "Signed"}>
                              <span className="flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" /> Close as Won</span>
                              <ChevronRight className="h-3 w-3 opacity-50" />
                            </Button>
                          </PermissionGate>
                          {quote.contract?.status !== "Signed" && (
                            <p className="text-[10px] text-warning flex items-center gap-1 pl-2">
                              <AlertTriangle className="h-2.5 w-2.5 shrink-0" /> Contract signature required to close as won
                            </p>
                          )}
                        </div>
                        <PermissionGate permission="leads_edit">
                          <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5 w-full justify-between border-warning/50 text-warning hover:bg-warning/10" onClick={() => setFollowUpTransitionOpen(true)}>
                            <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Schedule Follow-Up</span>
                            <ChevronRight className="h-3 w-3 opacity-50" />
                          </Button>
                        </PermissionGate>
                      </>
                    )}
                    {/* Follow-Up Needed → Won | Lost */}
                    {quote.stage === "Follow-Up Needed" && (
                      <>
                        <div className="space-y-1">
                          <PermissionGate permission="leads_edit">
                            <Button size="sm" className="h-9 text-xs gap-1.5 w-full justify-between bg-success hover:bg-success text-white disabled:opacity-50" onClick={() => setConvertDialogOpen(true)} disabled={quote.contract?.status !== "Signed"}>
                              <span className="flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" /> Close as Won</span>
                              <ChevronRight className="h-3 w-3 opacity-50" />
                            </Button>
                          </PermissionGate>
                          {quote.contract?.status !== "Signed" && (
                            <p className="text-[10px] text-warning flex items-center gap-1 pl-2">
                              <AlertTriangle className="h-2.5 w-2.5 shrink-0" /> Contract signature required to close as won
                            </p>
                          )}
                        </div>
                        <PermissionGate permission="leads_edit">
                          <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5 w-full justify-between border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => setLostDialogOpen(true)}>
                            <span className="flex items-center gap-1.5"><XCircle className="h-3.5 w-3.5" /> Close as Lost</span>
                            <ChevronRight className="h-3 w-3 opacity-50" />
                          </Button>
                        </PermissionGate>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Present / Contract Actions */}
            <Card className="shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Presentation & Contract</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {quote.projectConfig && !isContractSigned && (
                    <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5" onClick={() => setPresentViewOpen(true)}>
                      <Eye className="h-3.5 w-3.5" /> Present to Homeowner
                    </Button>
                  )}
                  {!isWonStatus(quote.status) && quote.status !== "Closed Lost" && quote.projectConfig && (!quote.contract || quote.contract.status === "Cancelled") && (
                    <PermissionGate permission="leads_edit">
                      <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5" onClick={() => setContractDialogOpen(true)}>
                        <Pen className="h-3.5 w-3.5" /> Sign Contract
                      </Button>
                    </PermissionGate>
                  )}
                  {quote.contract?.status === "Signed" && (
                    <PermissionGate permission="leads_edit">
                      <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5 text-destructive hover:text-destructive" onClick={() => setCancelContractOpen(true)}>
                        <XCircle className="h-3.5 w-3.5" /> Cancel Contract
                      </Button>
                    </PermissionGate>
                  )}
                </div>
                {!isWonStatus(quote.status) && quote.status !== "Closed Lost" && (!quote.contract || quote.contract.status !== "Signed") && (
                  <p className="text-[10px] text-muted-foreground italic mt-2 flex items-center gap-1">
                    <AlertTriangle className="h-2.5 w-2.5" /> Contract signature required before closing as won
                  </p>
                )}
                {isContractSigned && (
                  <p className="text-[10px] text-success italic mt-2 flex items-center gap-1">
                    <CheckCircle className="h-2.5 w-2.5" /> Contract already signed. Signature is locked; view signed contract details above.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Compare */}
            {canCompare && verificationComplete && (
              <Button variant="outline" size="sm" onClick={() => setComparisonView(true)} className="gap-1.5 w-full">
                <Layers className="h-3.5 w-3.5" /> Compare Quote Options
              </Button>
            )}

            {/* Timeline */}
            <Card className="shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Timeline</h2>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{formatStandardTimestamp(quote.createdAt)}</span></div>
                  {quote.presentedAt && <div className="flex justify-between"><span className="text-muted-foreground">Presented</span><span>{formatStandardTimestamp(quote.presentedAt)}</span></div>}
                  {quote.contract?.homeownerSignedAt && <div className="flex justify-between"><span className="text-muted-foreground">Contract Signed</span><span>{formatStandardTimestamp(quote.contract.homeownerSignedAt)}</span></div>}
                  {quote.wonAt && <div className="flex justify-between"><span className="text-muted-foreground">Won</span><span>{formatStandardTimestamp(quote.wonAt)}</span></div>}
                  {quote.lostAt && <div className="flex justify-between"><span className="text-muted-foreground">Lost</span><span>{formatStandardTimestamp(quote.lostAt)}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Last Updated</span><span>{formatStandardTimestamp(quote.updatedAt)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Created By</span><span>{createdByDisplay}</span></div>
                  {quote.convertedJobId && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Converted To</span>
                      <Link to={`/jobs/${quote.convertedJobId}`} className="text-primary font-medium hover:underline">{quote.convertedJobId}</Link>
                    </div>
                  )}
                </div>

                {/* Disposition */}
                {quote.lostReason && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Disposition</span>
                    </div>
                    <p className="text-xs font-medium">{quote.lostReason}</p>
                    {quote.notes && <p className="text-xs text-muted-foreground mt-1">{quote.notes}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
};

   /* ═══════════ RENDER ═══════════ */

  // Locked (Won/Lost) — show read-only summary
  if (isLocked) {
    return (
      <div className="space-y-5">
        <Link to="/opportunities" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Opportunities
        </Link>
        <Card className="p-3">
          <LifecycleTracker steps={[
            { label: "Lead", completed: true, active: false, timestamp: quote.createdAt },
            { label: "Appt Scheduled", completed: quote.stageHistory?.some(s => s.toStage === "Appointment Scheduled"), active: false },
            { label: "Appt Completed", completed: quote.stageHistory?.some(s => s.toStage === "Appointment Completed"), active: false },
            ...(quote.stageHistory?.some(s => s.toStage === "Follow-Up Needed") ? [{ label: "Follow-Up", completed: true, active: false }] : []),
            { label: quote.stage === "Closed Lost" ? "Closed Lost" : "Closed Won", completed: isClosedStatus(quote.stage), active: isClosedStatus(quote.stage), timestamp: quote.wonAt || quote.lostAt },
            ...(isWonStatus(quote.stage) && quote.linkedJobId ? [{ label: "Job Created", completed: true, active: true }] : []),
          ]} />
        </Card>

        {/* Won banner */}
        {(isWonStatus(quote.status) || isWonStatus(quote.status)) && quote.convertedJobId && (
          <Card className="shadow-none border-success/30 bg-success/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold flex items-center gap-1.5">
                    <CheckCircle className="h-4 w-4 text-success" /> Quote Won — Converted to Job
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    This quote is locked. All configuration and pricing are preserved.
                  </p>
                </div>
                <Link to={`/jobs/${quote.convertedJobId}`}>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    View {quote.convertedJobId} <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </div>
              {quote.deposit && (
                <div className="flex items-center gap-4 mt-2 pt-2 border-t text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><CreditCard className="h-3 w-3" /> Deposit: ${quote.deposit.amount.toLocaleString()}</span>
                  <span>{quote.deposit.method}</span>
                  <span>{quote.deposit.date}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {quote.status === "Closed Lost" && (
          <>
            <Card className="shadow-none border-destructive/30 bg-destructive/5">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-destructive" /> Opportunity Lost
                </h3>
                {quote.lostReason && <p className="text-xs text-muted-foreground mt-1"><span className="font-medium">Reason:</span> {quote.lostReason}</p>}
              </CardContent>
            </Card>
            <Card className="shadow-none border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 pb-2 mb-3 border-b">
                  <Shield className="h-4 w-4 text-primary" />
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Stage Transition</h2>
                </div>
                <PermissionGate permission="leads_edit">
                  <Button size="sm" variant="outline" className="h-9 text-xs gap-1.5 w-full justify-between border-warning/50 text-warning hover:bg-warning/10" onClick={handleReopenOpportunity}>
                    <span className="flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Reopen Opportunity</span>
                    <ChevronRight className="h-3 w-3 opacity-50" />
                  </Button>
                </PermissionGate>
              </CardContent>
            </Card>
          </>
        )}

        {/* Read-only summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Config summary */}
          <Card className="shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Configuration</h2>
                <Badge variant="outline" className="text-[9px] gap-1 ml-auto"><Lock className="h-2.5 w-2.5" /> Read-only</Badge>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {configSummaryItems.map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-1.5">
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                    <span className="text-xs font-medium text-right max-w-[60%]">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Info & timeline */}
          <div className="space-y-5">
            <Card className="shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Customer</h2>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{quote.customerName}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="font-medium">{quote.customerAddress}</span></div>
                  {quote.customerPhone && <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium">{quote.customerPhone}</span></div>}
                  {quote.customerEmail && <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{quote.customerEmail}</span></div>}
                </div>
              </CardContent>
            </Card>
            {quote.contract && <ContractDocumentView contract={quote.contract} />}
          </div>
        </div>
      </div>
    );
  }

  // Comparison mode
  if (comparisonView) {
    return (
      <div className="space-y-5">
        <Link to="/opportunities" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Quotes
        </Link>
        <QuoteComparison
          scenarios={quote.scenarios || []}
          baseConfig={quote.projectConfig}
          quoteId={quote.id}
          isLocked={isLocked}
          onScenariosChange={(scenarios) => updateQuote({ scenarios })}
          onSelectScenario={(scenario) => {
            updateQuote({
              projectConfig: scenario.config,
              totalEstimate: scenario.calculatedTotal,
            });
            setComparisonView(false);
          }}
          onBack={() => setComparisonView(false)}
        />
      </div>
    );
  }

  // ═══════════ TABBED RECORD ═══════════
  // "Appointment Mode" — when the rep is actively running the appointment
  // (Appt Scheduled / Appt Completed pre-sale), suppress opportunity-record
  // framing (Lifecycle Tracker, Action Required panel, Record Completeness).
  // Those belong to the Lead/Opportunity record layer, not the in-appointment
  // build & quote workspace.
  const isAppointmentMode =
    quote.stage === "Appointment Scheduled" ||
    quote.stage === "Appointment Completed" ||
    quote.stage === "Follow-Up Needed";

  return (
    <div className={cn("space-y-4", isAppointmentMode && "mx-auto w-full max-w-5xl")}>

      {/* ── Lifecycle Tracker ── (hidden in appointment mode) */}
      {!isAppointmentMode && (
      <Card className="p-3">
        <LifecycleTracker steps={[
          { label: "Lead", completed: true, active: quote.stage === "Lead", timestamp: quote.createdAt },
          { label: "Appt Scheduled", completed: quote.stageHistory?.some(s => s.toStage === "Appointment Scheduled"), active: quote.stage === "Appointment Scheduled" },
          { label: "Appt Completed", completed: quote.stageHistory?.some(s => s.toStage === "Appointment Completed"), active: quote.stage === "Appointment Completed" },
          ...(quote.stage === "Follow-Up Needed" || quote.stageHistory?.some(s => s.toStage === "Follow-Up Needed") ? [{ label: "Follow-Up", completed: quote.stage !== "Follow-Up Needed", active: quote.stage === "Follow-Up Needed" }] : []),
          { label: "Closed Won", completed: isWonStatus(quote.stage), active: false, timestamp: quote.wonAt },
        ]} />
      </Card>
      )}

      {/* ═══ Action Required (Lead/Opportunity record layer — hidden in appointment mode) ═══ */}
      {!isLocked && !isAppointmentMode && (() => {
        const blockers = getQuoteBlockers(quote);
        const nextSteps = getQuoteNextSteps(quote);
        const items: ActionRequiredItem[] = [];

        // Map blockers → action items with smart CTAs
        for (const b of blockers) {
          let primary: ActionRequiredItem["primary"] | undefined;
          let secondaryHref: string | undefined;

          if (b.category === "contract") {
            primary = { label: "Sign Contract", executionMode: "modal", openModal: () => setContractDialogOpen(true) };
          } else if (b.category === "data" || b.category === "compliance") {
            primary = { label: "Open Customer Info", executionMode: "direct", handler: () => setWizardStep(0) };
          } else if (b.category === "financial") {
            primary = { label: "Review Pricing", executionMode: "direct", handler: () => setWizardStep(2) };
          } else if (b.id.endsWith("-config")) {
            primary = { label: "Configure Scope", executionMode: "direct", handler: () => setWizardStep(1) };
          }

          items.push({
            id: b.id,
            severity: b.severity,
            label: b.label,
            detail: b.detail,
            primary,
            secondaryHref,
          });
        }

        // Stage-driven next-best-action (top suggestion only — avoid duplication)
        const stageAction: ActionRequiredItem | null = (() => {
          if (quote.stage === "Appointment Completed" && (!quote.contract || quote.contract.status !== "Signed")) {
            return {
              id: "stage-present",
              severity: "suggest",
              label: "Present & Close",
              detail: "Walk the homeowner through pricing and capture signature.",
              primary: { label: "Open Presentation", executionMode: "modal", openModal: () => setPresentViewOpen(true) },
            };
          }
          if (quote.stage === "Follow-Up Needed") {
            return {
              id: "stage-followup",
              severity: "suggest",
              label: "Follow-Up Due",
              detail: nextSteps[0]?.action || "Re-engage the customer to keep the deal warm.",
              primary: { label: "Send Follow-Up", executionMode: "modal", openModal: () => setPortalInviteOpen(true) },
              secondaryHref: `/follow-ups`,
            };
          }
          if (quote.stage === "Appointment Scheduled") {
            return {
              id: "stage-confirm",
              severity: "suggest",
              label: "Confirm Appointment",
              detail: "Reduce no-shows by confirming with the customer.",
              primary: { label: "Send Confirmation", executionMode: "modal", openModal: () => setPortalInviteOpen(true) },
            };
          }
          if (quote.stage === "Lead") {
            return {
              id: "stage-contact",
              severity: "suggest",
              label: "Make First Contact",
              detail: "New leads go cold within 24h — call or text now.",
              primary: { label: "Open Customer Info", executionMode: "direct", handler: () => setWizardStep(0) },
            };
          }
          return null;
        })();
        if (stageAction) items.push(stageAction);

        return (
          <ActionRequiredPanel
            items={items}
            maxItems={5}
            emptyState={{
              label: "Opportunity is on track",
              detail: "No blockers — keep momentum and follow up as scheduled.",
            }}
          />
        );
      })()}

      {/* ═══ Record Health Panel — Lead/Opportunity record layer (hidden in appointment mode) ═══ */}
      {!isLocked && !isAppointmentMode && (
        <RecordHealthPanel
          title="Record Completeness"
          blockers={[]}
          nextSteps={[]}
          completeness={[
            { label: "Customer Name", done: !!(quote.customerName || "").trim() },
            { label: "Phone", done: !!(quote.customerPhone || "").trim() },
            { label: "Address", done: !!(quote.customerAddress || "").trim() },
            { label: "Verification", done: !!quote.verification?.verificationComplete },
            { label: "Project Config", done: !!quote.projectConfig?.selectedVerticals?.length },
            { label: "Pricing", done: (computedTotal || quote.totalEstimate) > 0 },
            { label: "Contract", done: quote.contract?.status === "Signed" },
          ]}
          compact
        />
      )}

      {quote.contract?.status === "Cancelled" && quote.contract.cancellation && (
        <Card className="shadow-none border-warning/30 bg-warning/5">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              <XCircle className="h-4 w-4 text-warning" /> Contract Cancelled
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-medium">Reason:</span>{" "}
              {quote.contract.cancellation.reason === "other"
                ? quote.contract.cancellation.customReason
                : CANCEL_REASON_LABELS[quote.contract.cancellation.reason]}
            </p>
          </CardContent>
        </Card>
      )}

      {/* ═══ TABS ═══ */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto -mb-px">
        {WIZARD_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isActive = idx === wizardStep;
          const isComplete = isStepComplete(idx);
          return (
            <button
              key={step.id}
              onClick={() => setWizardStep(idx)}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                isActive
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
              )}
            >
              <Icon className={cn("h-3.5 w-3.5", isActive ? "text-primary" : "")} />
              {step.label}
              {isComplete ? (
                <Check className="h-3 w-3 text-success" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-warning/70" title="Incomplete" />
              )}
            </button>
          );
        })}
      </div>

      {/* ═══ TAB CONTENT ═══ */}
      <div className="min-h-[400px] pt-2">
        {renderWizardStep()}
      </div>

      {/* ── Contract Approval Dialog ── */}
      <ContractApprovalDialog
        open={contractDialogOpen}
        onOpenChange={setContractDialogOpen}
        quote={quote}
        finalTotal={computedTotal || quote.totalEstimate}
        onContractApproved={(contract) => {
          if (isContractSigned) {
            toast.info("Contract is already signed.");
            return;
          }
          updateQuote({ contract });
          // Auto-trigger portal invitation after signing
          setTimeout(() => setPortalInviteOpen(true), 600);
        }}
      />

      {/* ── Contract Cancel Dialog ── */}
      <ContractCancelDialog
        open={cancelContractOpen}
        onOpenChange={setCancelContractOpen}
        onCancel={handleContractCancel}
      />

      {/* ── Portal Invitation Dialog ── */}
      <PortalInvitationDialog
        open={portalInviteOpen}
        onOpenChange={setPortalInviteOpen}
        customerName={quote.customerName}
        customerEmail={quote.customerEmail}
        customerPhone={quote.customerPhone}
        jobId={quote.convertedJobId}
      />


      <CustomerQuoteView
        open={presentViewOpen}
        onOpenChange={setPresentViewOpen}
        quote={quote}
        onScheduleFollowUp={() => {
          setPresentViewOpen(false);
          setTimeout(() => setFollowUpTransitionOpen(true), 300);
        }}
        onContractApproved={(contract) => {
          if (isContractSigned) {
            toast.info("Contract is already signed.");
            setPresentViewOpen(false);
            return;
          }
          updateQuote({ contract });
          setPresentViewOpen(false);
          // Auto-trigger job creation using the deposit captured during signing
          const now = new Date().toISOString().slice(0, 10);
          void handleConfirmWon({
            amount: contract.depositAmount ?? 0,
            method: contract.paymentMethod === "Financing" ? "Financing" : "Cash / Check",
            date: now,
            notes: contract.paymentMethod === "Financing" ? "0 Down Financing" : "Deposit from contract signing",
            contractDocument: contract,
          });
        }}
      />

      {/* ── Mark as Won / Convert Dialog ── */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-success" /> Close Quote as Won
            </DialogTitle>
            <DialogDescription>
              Capture the deposit and create a job. The quote will be locked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{quote.customerName}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Project</span><span className="font-medium">{quote.projectConfig?.selectedVerticals?.join(", ") || "General"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Final Total</span><span className="font-bold text-primary">${(computedTotal || quote.totalEstimate).toLocaleString()}</span></div>
            </div>

            {!conversionPreflight.canConvert && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
                <p className="text-xs font-semibold text-destructive flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Conversion blocked
                </p>
                <ul className="text-[11px] text-destructive/90 list-disc pl-4 space-y-1">
                  {conversionPreflight.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
                {conversionPreflight.missingCatalogSkus.length > 0 && (
                  <p className="text-[10px] text-destructive/80">
                    Missing SKU refs: {conversionPreflight.missingCatalogSkus.slice(0, 6).join(", ")}
                    {conversionPreflight.missingCatalogSkus.length > 6 ? ", ..." : ""}
                  </p>
                )}
                {conversionPreflight.unresolvedCustomItems.length > 0 && (
                  <p className="text-[10px] text-destructive/80">
                    Unmapped custom items: {conversionPreflight.unresolvedCustomItems.slice(0, 4).map((row) => row.itemName || "(blank)").join(", ")}
                    {conversionPreflight.unresolvedCustomItems.length > 4 ? ", ..." : ""}
                  </p>
                )}
              </div>
            )}

            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                <p className="text-xs font-bold">Deposit / Down Payment</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Method *</Label>
                  <Select value={depositMethod} onValueChange={handleDepositMethodChange}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Amount *</Label>
                  <div className="relative mt-1">
                    <Input type="number" className="h-8 text-xs pl-6" placeholder="0"
                      value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} min="0" />
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                  </div>
                  {depositMethod === "Financing" && depositAmount === "0" && (
                    <p className="text-[9px] text-success mt-0.5 font-medium">0 Down Financing</p>
                  )}
                  {depositMethod && depositMethod !== "Financing" && (parseFloat(depositAmount) || 0) <= 0 && (
                    <p className="text-[9px] text-destructive mt-0.5 font-medium">
                      {depositMethod} requires a deposit &gt; $0
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" className="mt-1 h-8 text-xs" value={depositDate} onChange={(e) => setDepositDate(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input className="mt-1 h-8 text-xs" placeholder="Optional" value={depositNotes} onChange={(e) => setDepositNotes(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold">Permit</p>
                  <p className="text-[11px] text-muted-foreground">
                    {permitAssessment.reasons.length > 0
                      ? `Auto-detected — ${permitAssessment.reasons.join(" • ")}`
                      : "Set in Review & Present tab. Override here if needed."}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground">{permitRequiredOverride ? "Required" : "Not Required"}</span>
                  <Switch checked={permitRequiredOverride} onCheckedChange={setPermitRequiredOverride} />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/20 p-2 rounded">
              <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Quote will be locked. Configuration and pricing will become read-only.</span>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button
              className="gap-1.5 bg-success hover:bg-success"
              onClick={() => handleConfirmWon()}
              disabled={!conversionPreflight.canConvert}
            >
              <CheckCircle className="h-3.5 w-3.5" /> Confirm & Create Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Schedule Follow-Up Dialog (Stage Transition) ── */}
      <Dialog open={followUpTransitionOpen} onOpenChange={setFollowUpTransitionOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-warning" /> Schedule Follow-Up
            </DialogTitle>
            <DialogDescription>
              Document why the deal didn't close today and set a follow-up date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Disposition Reason *</Label>
              <Select value={followUpDispositionReason} onValueChange={setFollowUpDispositionReason}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select reason…" /></SelectTrigger>
                <SelectContent>
                  {DISPOSITION_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Follow-Up Type *</Label>
              <Select value={followUpType} onValueChange={setFollowUpType}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rep">Sales Rep Follow-Up</SelectItem>
                  <SelectItem value="manager">Manager Follow-Up</SelectItem>
                </SelectContent>
              </Select>
              {followUpType === "manager" && (
                <p className="text-[9px] text-warning mt-1 font-medium">Will notify Sales Manager, Admin, and Super Admin</p>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold">Follow-Up Due Date *</Label>
              <Input type="date" className="mt-1 h-8 text-xs" value={followUpDueDate} onChange={(e) => setFollowUpDueDate(e.target.value)} />
              <p className="text-[9px] text-muted-foreground mt-0.5">Default: 2 business days</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Notes</Label>
              <Textarea className="mt-1 text-xs" rows={2} value={followUpNotes} onChange={(e) => setFollowUpNotes(e.target.value)} placeholder="Additional context for follow-up…" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setFollowUpTransitionOpen(false)}>Cancel</Button>
            <Button className="gap-1.5 bg-warning hover:bg-warning text-white" onClick={handleConfirmFollowUp} disabled={!followUpDispositionReason || !followUpType || !followUpDueDate}>
              <CalendarClock className="h-3.5 w-3.5" /> Save Follow-Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Mark as Lost Dialog ── */}
      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" /> Mark Quote as Lost
            </DialogTitle>
            <DialogDescription>
              Provide a reason for losing this quote.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold">Reason *</Label>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select reason…" /></SelectTrigger>
              <SelectContent>
                {DISPOSITION_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setLostDialogOpen(false); setLostReason(""); }}>Cancel</Button>
            <Button variant="destructive" className="gap-1.5" onClick={handleConfirmLost} disabled={!lostReason.trim()}>
              <XCircle className="h-3.5 w-3.5" /> Confirm Lost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
